import * as t from 'io-ts';
import { golfStrokeSummary, golfMatchPlaySummary } from './golf';
import { optional } from './helper';

const scoreValues = t.type({
  home: optional(t.number),
  away: optional(t.number),
});

const winner = t.type({
  side: t.keyof({ home: 0, away: 0, draw: 0 }),
});

const score = t.type({
  total: scoreValues,
  shootout: optional(scoreValues),
  winner: optional(winner),
});

export const status = t.type({
  id: t.number,
  name: t.string,
});
export type Status = t.TypeOf<typeof status>;
const duration = t.type({
  minutes: t.number,
  additionalMinutes: t.number,
  seconds: t.number,
});

const gameStatus = t.intersection([
  status,
  t.type({
    shortName: t.string,
    isActive: t.keyof({ False: 0, True: 0 }),
  }),
]);

const rugbyTeamObj = {
  name: t.string,
  icon: optional(t.string),
};
const rugbyTeam = t.type(rugbyTeamObj);
export type RugbyTeam = t.TypeOf<typeof rugbyTeam>;

const footballTeamObj = {
  ...rugbyTeamObj,
  shortName: t.string,
};

const cricketTeamObj = {
  ...footballTeamObj,
  teamId: t.string,
};

const team = t.type(footballTeamObj);
export type Team = t.TypeOf<typeof team>;

const tournament = t.type({
  name: t.string,
  icon: optional(t.string),
});

const channel = t.type({
  title: t.string,
  shortName: t.string,
  channelNumber: t.number,
  icon: t.string,
  liveIcon: t.string,
});

const channelDetail = t.type({
  channel,
});

const footballSummary = t.type({
  eventDateStart: t.string,
  score: optional(score),
  status,
  duration: optional(duration),
  gameStatus: optional(gameStatus),
  subStatus: status,
  scoreCoverage: status,
  teams: t.type({
    away: team,
    home: team,
  }),
  eventId: t.string,
  tournament: t.intersection([tournament, t.type({
    leaguePath: t.string,
  })]),
  venueName: t.string,
  channelDetails: optional(t.array(channelDetail)),
  feedId: t.string,
});
export type FootballSummaryApi = t.TypeOf<typeof footballSummary>;

export const venueNullName = t.type({
  venueName: optional(t.string),
});

export const rugbySummary = t.type({
  eventDateStart: t.string,
  score: t.type({
    total: scoreValues,
  }),
  status,
  matchSubStatus: optional(t.string),
  rugbyTeam: t.type({
    away: t.type(rugbyTeamObj),
    home: t.type(rugbyTeamObj),
  }),
  eventId: t.string,
  tour: tournament,
  venue: venueNullName,
  channelDetails: optional(t.array(channelDetail)),
  feedId: t.string,
});
export type RugbySummaryApi = t.TypeOf<typeof rugbySummary>;

const inningsScores = t.type({
  declared: t.boolean,
  isEliminator: optional(t.boolean),
  followOn: t.boolean,
  overs: t.number,
  runs: t.number,
  wickets: t.number,
});
export type InningScore = t.TypeOf<typeof inningsScores>;

const cricketScores = t.type({
  inningsScores: t.array(inningsScores),
  team: t.type(cricketTeamObj),
});

const matchState = t.type({
  day: t.number,
  days: t.number,
});

const cricketSummary = t.type({
  eventDateStart: t.string,
  eventDateEnd: t.string,
  status,
  subStatus: optional(status),
  gameType: status,
  homeTeamId: t.string,
  resultDescription: optional(t.string),
  scores: t.tuple([cricketScores, cricketScores]),
  matchState: optional(matchState),
  battingTeamId: optional(t.string),
  winnerTeamId: optional(t.string),
  eventId: t.string,
  tour: tournament,
  eventName: t.string,
  venue: venueNullName,
  channelDetails: optional(t.array(channelDetail)),
  toss: optional(t.type({
    decision: t.string,
    description: t.string,
    winnerTeamId: t.string,
  })),
});
export type CricketSummaryApi = t.TypeOf<typeof cricketSummary>;

const motorsportRaceResult = t.type({
  Initials: t.string,
  LastName: t.string,
  FullName: t.string,
  Time: optional(t.string),
  Country: optional(t.string),
  TeamName: t.string,
});
export type MotorsportRaceResult = t.TypeOf<typeof motorsportRaceResult>;

const venue = t.type({
  venueName: t.string,
});

const motorsportSummary = t.type({
  tour: tournament,
  id: t.string,
  event: t.type({
    eventId: t.string,
    icon: t.string,
    id: t.string,
    name: t.string,
  }),
  shortName: t.string,
  venue,
  city: t.string,
  country: optional(t.string),
  status,
  subStatus: t.string,
  eventDateStart: t.string,
  startTime: t.string,
  eventDateEnd: t.string,
  winner: optional(t.string),
  grid: optional(t.array(t.type({
    Time: optional(t.string),
    LastName: t.string,
    Initials: t.string,
    FullName: t.string,
    Country: optional(t.string),
    TeamName: t.string,
  }))),
  raceResult: optional(t.array(motorsportRaceResult)),
  feedId: t.string,
  channelDetails: optional(t.array(channelDetail)),
});
export type MotorsportSummaryApi = t.TypeOf<typeof motorsportSummary>;

const tour = t.type({
  name: t.string,
  icon: t.string,
});

const setSides = t.type({
  games: optional(t.number),
  tiebreak: optional(t.number),
});

const teamsSets = t.type({
  a: setSides,
  b: setSides,
});

const teams = t.type({
  side: optional(t.string),
  sets: optional(t.number),
  players: t.array(t.string),
});

const teamMatches = t.type({
  winner: optional(t.string),
  sets: t.array(teamsSets),
  teams: t.array(teams),
});
export type TennisMatchResults = t.TypeOf<typeof teamMatches>;

const round = t.type({
  matches: optional(t.array(teamMatches)),
  roundTitle: optional(t.string),
});

const tennisSummary = t.type({
  eventDateStart: t.string,
  eventDateEnd: t.string,
  status,
  subStatus: optional(status),
  round: optional(round),
  surfaceType: t.string,
  tourName: t.string,
  tourTitle: t.string,
  eventId: t.string,
  tour,
  gender: t.string,
  matchType: t.string,
  tournament,
  icon: t.string,
  eventName: t.string,
  venue: venueNullName,
  feedId: t.string,
  channelDetails: optional(t.array(channelDetail)),
});
export type TennisSummaryApi = t.TypeOf<typeof tennisSummary>;

export const summaryApiResponse = t.union([
  footballSummary,
  rugbySummary,
  cricketSummary,
  motorsportSummary,
  golfStrokeSummary,
  golfMatchPlaySummary,
  tennisSummary,
]);
export type SummaryApi = t.TypeOf<typeof summaryApiResponse>;
